import { HttpClientRequestInit } from "@data-types/http/httpClientRequestInit";

import { MainHttpClient, mainHttpClient } from "@utils/http/mainHttpClient";
import { mTLSProxy } from "@utils/mtls/mtlsPlugin";
import { mapNonMtlsUrlToMtls } from "@utils/http/mtlsUrlMapper";

export interface MtlsFlags {
	disableHtmlCheck?: boolean;
	disableCertValidation?: boolean;
	disableRetryOnAnyError?: boolean;
}

export class MtlsHttpClient extends MainHttpClient {
	private fallbackClient: MainHttpClient;
	private flags: MtlsFlags = {
		disableCertValidation: true,
		disableRetryOnAnyError: true,
	};

	constructor() {
		super();
		this.fallbackClient = mainHttpClient;
	}

	private async fileToBase64(file: File): Promise<string> {
		const chunkSize = 1024 * 1024; // 1MB chunks
		if (file.size > chunkSize * 10) {
			return this.fileToBase64Chunked(file);
		}

		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => {
				const result = reader.result as string;

				const base64Data = result.split(",")[1];
				resolve(base64Data);
			};
			reader.onerror = () => reject(reader.error);
			reader.readAsDataURL(file);
		});
	}

	private async fileToBase64Chunked(file: File): Promise<string> {
		const chunkSize = 1024 * 1024; // 1MB chunks
		const chunks: string[] = [];

		for (let start = 0; start < file.size; start += chunkSize) {
			const end = Math.min(start + chunkSize, file.size);
			const chunk = file.slice(start, end);

			const base64Chunk = await new Promise<string>((resolve, reject) => {
				const reader = new FileReader();
				reader.onload = () => {
					const result = reader.result as string;
					const base64Data = result.split(",")[1];
					resolve(base64Data);
				};
				reader.onerror = () => reject(reader.error);
				reader.readAsDataURL(chunk);
			});

			chunks.push(base64Chunk);
		}

		return chunks.join("");
	}

	async _internalRequest(
		url: RequestInfo | URL,
		options?: HttpClientRequestInit
	): Promise<Response> {
		const originalUrl = url.toString();
		const mtlsUrl = mapNonMtlsUrlToMtls(originalUrl);

		if (!mtlsUrl) {
			return this.fallbackClient._internalRequest(originalUrl, options);
		}
		try {
			delete options?.signal;

			let processedOptions = { ...options };
			if (options?.body instanceof FormData) {
				const formData = options.body as FormData;
				const formDataObject: any = {
					_isFormData: true,
					fields: [],
					files: [],
				};

				for (const [key, value] of formData.entries()) {
					if (value instanceof File) {
						const maxFileSize = 10 * 1024 * 1024; // 10MB limit (reduced due to mTLS plugin limitations)
						if (value.size > maxFileSize) {
							throw new Error(
								`File ${value.name} is too large (${Math.round(value.size / 1024 / 1024)}MB). Maximum size is ${maxFileSize / 1024 / 1024}MB.`
							);
						}

						const base64 = await this.fileToBase64(value);

						formDataObject.files.push({
							name: key,
							filename: value.name,
							contentType:
								value.type || "application/octet-stream",
							data: base64,
						});
					} else {
						formDataObject.fields.push({
							name: key,
							value: value,
						});
					}
				}
				processedOptions.body = JSON.stringify(formDataObject);
				processedOptions.headers = {
					...processedOptions.headers,
					"Content-Type": "application/json",
					"X-Original-Content-Type": "multipart/form-data",
				};
			}

			let response;
			try {
				response = await mTLSProxy.fetch({
					url: mtlsUrl,
					options: {
						request: processedOptions,
						_clientOptions: this.flags,
					},
				});
			} catch (mtlsError) {
				console.error(
					"mTLS plugin error (possibly large file):",
					mtlsError
				);

				if (
					mtlsError.message &&
					mtlsError.message.includes("Maximum call stack")
				) {
					throw new Error("File too large for mTLS processing");
				}
				throw mtlsError;
			}

			if (
				response &&
				typeof response === "object" &&
				"status" in response
			) {
				const status = (response as any).status || 200;
				const responseInit: ResponseInit = {
					status,
					statusText: (response as any).statusText || "OK",
					headers: (response as any).headers || {},
				};

				const shouldHaveNoBody =
					status === 204 ||
					status === 304 ||
					(status >= 100 && status < 200);

				let res: Response;
				if (shouldHaveNoBody) {
					res = new Response(null, responseInit);
				} else {
					const base64String = response.base64data;
					const binaryString = atob(base64String);
					const bytes = new Uint8Array(binaryString.length);
					for (let i = 0; i < binaryString.length; i++) {
						bytes[i] = binaryString.charCodeAt(i);
					}

					const text = new TextDecoder().decode(bytes);
					const body = text || "";
					res = new Response(body, responseInit);
				}

				return res;
			}

			return response;
		} catch (error) {
			if (
				!this.flags.disableCertValidation &&
				error?.message?.includes("TLS_ERROR")
			) {
				console.error(
					"client certificate not validated by server:",
					error
				);
				return this.fallbackClient._internalRequest(
					originalUrl,
					options
				);
			}

			if (
				!this.flags.disableHtmlCheck &&
				error?.message?.includes("HTML_RESPONSE_ERROR")
			) {
				console.error(
					"mTLS received HTML response, falling back to regular HTTP client:",
					error
				);
				return this.fallbackClient._internalRequest(
					originalUrl,
					options
				);
			}
			if (
				!this.flags.disableRetryOnAnyError &&
				error?.message?.includes("MTLS_RETRY")
			) {
				console.error("mtls Retry:", error);
				return this.fallbackClient._internalRequest(
					originalUrl,
					options
				);
			}

			console.error("mTLS fetch failed:", error);
			throw error;
		}
	}
}

const mtlsHttpClient = new MtlsHttpClient();

export { mtlsHttpClient };
